import { FormSectionHandler } from '../entity.handler';
import { ClientResponse, ClientUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, TagsField, EMPTY_GUID } from '../utils';
import { COMPANY_ICONS } from '../icons';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    name?: string;
    website?: string;
    tags?: { id: string, name: string }[]; // format required for tag input
    group?: string;
    pin?: string;
    timeZoneId?: string;
    language?: string;
}

export class ClientDetailsSectionHandler implements FormSectionHandler<ClientResponse, EditorValues> {

    type = 'form';
    icon = COMPANY_ICONS.client;
    bulk = true;
    readonly = this.admin.user.owner.type === 'client';


    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    private properties = ['name', 'website', 'tags', 'group', 'timeZoneId'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.CLIENT.DETAILS.${key}`, values);
    }

    applyUpdate(entity: ClientResponse, payload: ClientUpdateRequest): Promise<ClientResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload), { _navigation: 'client' }).then(client => {
            this.admin.clientUpdated(client);
            return client;
        });
    }

    mapEntityToEditor(entity: ClientResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>([...this.properties, 'pin'], entity, {});
        result.pin = result.pin && result.pin.substr(0, 4) + '-' + result.pin.substr(4);
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: any, values: EditorValues): Promise<any> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    getFields(entity: ClientResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.readonly ? null : this.admin.getCached(`listCompanyGroups/${this.ownerId}`, api => api.accounts.listCompanyGroups(this.ownerId)).catch(nullAccessDenied),
            this.admin.getTimeZones(),
        ]).then(([clientGroups, timeZones]) => {
            const groups = clientGroups && [{ key: EMPTY_GUID, value: 'None' }, ...clientGroups.items.sort((a, b) => a.name.localeCompare(b.name)).map(x => ({ key: x.id, value: x.name }))];
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                TextField('website', this.translate('FIELDS.WEBSITE'), true, 1, 255),
                groups && ComboField('group', this.translate('FIELDS.GROUP'), false, groups),
                ComboField('timeZoneId', this.translate('FIELDS.TIMEZONE'), false, timeZones.map(x => ({ key: x.id, value: x.name }))),
                TagsField('tags', this.translate('FIELDS.TAGS'), false),
                !this.creating && { ...TextField('pin', this.translate('FIELDS.PIN'), false, 1, 255), readonly: true },
            ].filter(x => x);

        }).then(fields => EntityUtils.prepareBulk(!entity, ['group', 'timeZoneId'], fields));
    }

}
